import React, { useState, useMemo, useEffect, useRef } from 'react';
import './RoomDetails.css';
import DOMPurify from 'dompurify';
import { format, parseISO } from 'date-fns';

const FeaturePopover = ({ content, targetRef, onClose }) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (targetRef && popoverRef.current) {
      const targetRect = targetRef.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      
      let top = targetRect.bottom + 8;
      let left = targetRect.left;

      // Adjust if it goes off-screen
      if (left + popoverRect.width > window.innerWidth - 16) {
        left = window.innerWidth - popoverRect.width - 16;
      }
      if (top + popoverRect.height > window.innerHeight) {
        top = targetRect.top - popoverRect.height - 8;
      }

      setPosition({ top, left });
    }
  }, [targetRef]);

  return (
    <>
      <div className="popover-overlay" onClick={onClose}></div>
      <div ref={popoverRef} className="feature-popover" style={{ top: position.top, left: position.left }}>
        <p>{content}</p>
        <button onClick={onClose} className="popover-close-btn">OK</button>
      </div>
    </>
  );
};

const RateItem = ({ ratePackage, isSelected, onSelect, onShowDetails }) => {
  const { rate } = ratePackage;
  
  return (
    <div className={`rate-item ${isSelected ? 'selected' : ''}`} onClick={() => onSelect(rate.id)}>
      <div className="rate-radio">
        <div className="radio-dot"></div>
      </div>
      <div className="rate-info">
        <div className="rate-name-price">
          <div className="rate-price">${rate.nightly_before_tax}
            <span className="price-per-night"> Avg nightly, excl. fees</span>
          </div>
          <h3 className="rate-name">{rate.name}</h3>
        </div>
        <p className="rate-description">
          {DOMPurify.sanitize(rate.description, { ALLOWED_TAGS: [] })}
        </p>
        <button className="rate-details-button" onClick={(e) => {
          e.stopPropagation();
          onShowDetails(rate, 'Rate Details & Policies');
        }}>Rate details and policies</button>
      </div>
    </div>
  );
};

const RoomDetails = ({ 
  room, 
  onClose, 
  onShowPriceBreakdown, 
  checkInDate, 
  checkOutDate, 
  guests, 
  onBookNow,
  selectedRateId,
  setSelectedRateId,
  allRoomsData,
  onUpgrade,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isRatesExpanded, setIsRatesExpanded] = useState(false);
  const [originalRate, setOriginalRate] = useState(null);
  const [popover, setPopover] = useState({ visible: false, content: null, targetRef: null });
  const [selectedRateUpgradeService, setSelectedRateUpgradeService] = useState(null);
  const cancellationInfoRef = useRef(null);

  useEffect(() => {
    if (room && room.rate) {
      setOriginalRate(room.rate);
    }
  }, [room]);

  const { primaryRatePackage, otherRatePackages } = useMemo(() => {
    if (!room) return { primaryRatePackage: null, otherRatePackages: [] };

    const allRatePackages = [];
    if (room.rate) {
        allRatePackages.push({
            is_package: false,
            rate: room.rate,
            upgrade_service: room.upgrade_service
        });
    }
    if (room.packages) {
        room.packages.forEach(p => {
            if (p.rate) {
                allRatePackages.push({
                    is_package: true,
                    rate: p.rate,
                    upgrade_service: p.upgrade_service
                });
            }
        });
    }

    if (allRatePackages.length === 0) return { primaryRatePackage: null, otherRatePackages: [] };

    let defaultPrimaryRatePackage;
    const flexibleRatePackage = allRatePackages.find(p => p.rate.name && p.rate.name.trim().toLowerCase() === 'flexible rate');
    
    if (flexibleRatePackage) {
      defaultPrimaryRatePackage = flexibleRatePackage;
    } else {
      defaultPrimaryRatePackage = [...allRatePackages].sort((a, b) => (a.rate.nightly_before_tax || 0) - (b.rate.nightly_before_tax || 0))[0];
    }

    let displayedPrimaryRatePackage = defaultPrimaryRatePackage;
    const selectedRatePackage = allRatePackages.find(p => p.rate.id === selectedRateId);

    if (selectedRatePackage && selectedRatePackage.rate.id !== defaultPrimaryRatePackage.rate.id) {
      displayedPrimaryRatePackage = selectedRatePackage;
    }

    const otherRatePackages = allRatePackages.filter(p => p.rate.id !== displayedPrimaryRatePackage.rate.id);

    otherRatePackages.sort((a, b) => (a.rate.nightly_before_tax || 0) - (b.rate.nightly_before_tax || 0));

    return { primaryRatePackage: displayedPrimaryRatePackage, otherRatePackages };
  }, [room, selectedRateId]);

  // Set the default selected rate
  useEffect(() => {
    if (primaryRatePackage && (!selectedRateId || ![primaryRatePackage, ...otherRatePackages].some(p => p && p.rate.id === selectedRateId))) {
      setSelectedRateId(primaryRatePackage.rate.id);
    }
  }, [primaryRatePackage, otherRatePackages, selectedRateId, setSelectedRateId]);
  
  const selectedRatePackage = useMemo(() => {
    const all = primaryRatePackage ? [primaryRatePackage, ...otherRatePackages] : [...otherRatePackages];
    return all.find(p => p && p.rate.id === selectedRateId);
  }, [primaryRatePackage, otherRatePackages, selectedRateId]);

  useEffect(() => {
    if (selectedRatePackage && selectedRatePackage.upgrade_service && !selectedRatePackage.upgrade_service.next_rate_plan_code) {
      setSelectedRateUpgradeService(selectedRatePackage.upgrade_service);
    } else {
      setSelectedRateUpgradeService(null);
    }
  }, [selectedRatePackage]);

  const selectedRate = selectedRatePackage?.rate;

  const handleImageNavigation = (direction) => {
    const totalImages = room.room.carousel.length;
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentImageIndex + 1) % totalImages;
    } else {
      newIndex = (currentImageIndex - 1 + totalImages) % totalImages;
    }
    setCurrentImageIndex(newIndex);
  };

  const formattedDateRange = () => {
    if (checkInDate && checkOutDate) {
      const start = parseISO(checkInDate);
      const end = parseISO(checkOutDate);
      const formatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
      const checkinStr = start.toLocaleDateString('en-US', formatOptions);
      const checkoutStr = end.toLocaleDateString('en-US', formatOptions);
      return `${checkinStr} - ${checkoutStr}`;
    }
    return '';
  };

  const totalGuests = guests ? guests.adults + guests.children : 0;

  const currentImage = room.room.carousel[currentImageIndex];

  const generateUpgradeOption = (currentRoomData, allRoomData, currentRate) => {
    if (!currentRate) return null;
    
    // Sort all rooms by price to find the next available upgrade
    const sortedRooms = [...allRoomData].sort((a, b) => 
      (a.rate?.nightly_before_tax || 0) - (b.rate?.nightly_before_tax || 0)
    );

    // Find the index of the current room in the sorted list
    const currentRoomIndex = sortedRooms.findIndex(r => r.room.id === currentRoomData.room.id);

    // Find the next room in the sorted list
    if (currentRoomIndex === -1 || currentRoomIndex === sortedRooms.length - 1) {
      return null; // No upgrade available or current room not found
    }
    
    const higherTierRoom = sortedRooms[currentRoomIndex + 1];

    if (!higherTierRoom || !higherTierRoom.rate) {
      return null; // No upgrade available
    }
    
    // Calculate price difference
    const priceDifference = (higherTierRoom.rate.nightly_before_tax || 0) - (currentRate.nightly_before_tax || 0);
    
    if (priceDifference <= 0) {
      return null; // Not really an upgrade
    }

    return {
      type: 'upgrade',
      title: 'Upgrade room category',
      subtitle: higherTierRoom.room.name,
      priceText: `$${Math.ceil(priceDifference)}`,
      priceSubtitle: 'per night',
      targetRoom: higherTierRoom,
      state: 'available' // available, selected, disabled
    };
  }

  const roomOptions = useMemo(() => {
    const options = [];
    const upgradeOption = generateUpgradeOption(room, allRoomsData, originalRate);
    if (upgradeOption) {
      options.push(upgradeOption);
    }
    return options;
  }, [room, allRoomsData, originalRate]);

  const handleOptionSelect = (option) => {
    if (option.type === 'upgrade') {
      onUpgrade(option.targetRoom);
    }
  };

  const handleShowFeatureDetails = (content, targetRef) => {
    setPopover({ visible: true, content, targetRef });
  };

  const handleClosePopover = () => {
    setPopover({ visible: false, content: null, targetRef: null });
  };

  if (!room) {
    return null;
  }
  
  return (
    <div className="room-details-view">
      <div className="details-header">
        <button className="ios-nav-back" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#3D4E40" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6" />
            </svg>
        </button>
        <h1 className="details-title">{room.room.name}</h1>
        <div className="ios-nav-right"></div>
      </div>
      <div className="sub-header-bar">
        <div className="date-guest-info">
            <span className="info-item">
                <svg className="icon" viewBox="0 0 24 24"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>
                <span>{formattedDateRange()}</span>
            </span>
            {selectedRate && selectedRate.nights_count > 0 && (
              <span className="info-item">
                <svg className="icon" viewBox="0 0 24 24"><path d="M21 10.78V8a3 3 0 0 0-3-3H6A3 3 0 0 0 3 8v2.78A2.25 2.25 0 0 0 2 13v6a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h14v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-6a2.25 2.25 0 0 0-1-2.22zM5 8a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v2H5zm14 7H5v-3h14z"/></svg>
                <span>{selectedRate.nights_count} night{selectedRate.nights_count > 1 ? 's' : ''}</span>
              </span>
            )}
            {totalGuests > 0 && (
              <span className="info-item">
                <svg className="icon" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                <span>{totalGuests} {totalGuests === 1 ? 'guest' : 'guests'}</span>
              </span>
            )}
        </div>
      </div>
      <div className="details-content">
        <div className="details-image-container">
          <img src={currentImage} alt={`${room.room.name} - Image ${currentImageIndex + 1}`} className="details-room-image" />
          {room.room.carousel.length > 1 && (
            <>
              <button className="details-image-nav prev" onClick={() => handleImageNavigation('prev')}>
                <svg viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <button className="details-image-nav next" onClick={() => handleImageNavigation('next')}>
                <svg viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
              </button>
              <div className="details-image-counter">{currentImageIndex + 1} / {room.room.carousel.length}</div>
            </>
          )}
        </div>
        
        <div className="description-section">
          <p className={`room-full-description ${isDescriptionExpanded ? 'expanded' : ''}`}>
            {room.room.detailed_description}
          </p>
          <button 
            className="read-more-button"
            onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
          >
            {isDescriptionExpanded ? 'Read Less' : 'Read More'}
          </button>
        </div>

        {selectedRateUpgradeService && (
          <div className="cancellation-details-section">
            <svg className="cancellation-icon" viewBox="0 0 24 24"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
            <div className="cancellation-text">
              <h4>{selectedRateUpgradeService.title}</h4>
            </div>
            <button 
              ref={cancellationInfoRef}
              className="cancellation-info-button" 
              onClick={() => handleShowFeatureDetails(selectedRateUpgradeService.description, cancellationInfoRef.current)}
            >
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>
            </button>
          </div>
        )}
        
        <div className="rates-section">
          <h2 className="section-title">RATES AND PACKAGES</h2>
          <div className="rates-list">
            {primaryRatePackage && (
              <RateItem 
                ratePackage={primaryRatePackage}
                isSelected={selectedRateId === primaryRatePackage.rate.id}
                onSelect={setSelectedRateId}
                onShowDetails={(rate, title) => onShowPriceBreakdown(rate, title, room)}
              />
            )}
            {isRatesExpanded && otherRatePackages.map(pkg => (
              <RateItem 
                key={pkg.rate.id}
                ratePackage={pkg}
                isSelected={selectedRateId === pkg.rate.id}
                onSelect={setSelectedRateId}
                onShowDetails={(rate, title) => onShowPriceBreakdown(rate, title, room)}
              />
            ))}
          </div>
          {otherRatePackages.length > 0 && (
            <button className="toggle-rates-button" onClick={() => setIsRatesExpanded(!isRatesExpanded)}>
              {isRatesExpanded ? `Show less rates` : `Show ${otherRatePackages.length} more rates`}
            </button>
          )}
        </div>

        {roomOptions.length > 0 && (
          <div className="room-options-section">
            <h2 className="section-title">ENHANCE YOUR STAY</h2>
            <div className="room-options">
                {roomOptions.map((option, index) => (
                    <div 
                      key={index} 
                      className={`option-row ${option.state}`}
                      onClick={() => handleOptionSelect(option)}
                    >
                        <div className="option-select">
                          <svg className="option-checkmark" viewBox="0 0 24 24"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>
                        </div>
                        <div className="option-content">
                            <span className="option-title">{option.title}</span>
                            <span className="option-subtitle">{option.subtitle}</span>
                        </div>
                        <div className="option-price">
                            <span className="price-text">{option.priceText}</span>
                            <span className="price-subtitle">&nbsp;{option.priceSubtitle}</span>
                        </div>
                    </div>
                ))}
            </div>
          </div>
        )}
      </div>
      {selectedRate && (
        <div className="pricing-footer">
          <div className="price-container-footer" onClick={() => onShowPriceBreakdown(selectedRate, 'Price Breakdown', room)}>
            <div className="price-line-1">
              <span className="price-value-footer">${selectedRate.total_after_tax?.toFixed(2)}</span>
            </div>
            <span className="price-breakdown-action">Price breakdown</span>
          </div>
          <button className="confirm-button" onClick={() => onBookNow({ ...room, rate: selectedRate })}>
            Confirm
          </button>
        </div>
      )}

      {popover.visible && (
        <FeaturePopover 
          content={popover.content}
          targetRef={popover.targetRef}
          onClose={handleClosePopover}
        />
      )}
    </div>
  );
};

export default RoomDetails; 